import {
  Code,
  Code2,
  Activity,
  Printer,
  FolderGit2,
  Loader,
  Network,
  FileDown,
  Copy,
  Check,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type {
  DriftReport,
  MaintenanceScoreResult,
  RepositoryAnalysis,
} from '../types/analysis.types';
import { formatCount, parseDateOnly } from '../../utils/formatters';
import { ScoreCircle } from './scores';
import { ProjectList } from './ProjectList';

type ScoreTab = 'sdlc' | 'drift';

interface OverviewRepositoryHeaderProps {
  data: RepositoryAnalysis;
  jobId: string | undefined;
  isDevelopment: boolean;
  showDebugJson: boolean;
  onToggleDebugJson: () => void;
  isExporting: boolean;
  onDownloadReport: () => void;
  onCopyAgentMd: () => void;
  onDownloadAgentMd: () => void;
  agentMdBusy?: boolean;
  agentMdCopied: boolean;
  agentMdEnabled: boolean;
  maintenanceScore?: MaintenanceScoreResult | null;
  driftData?: DriftReport | null;
  driftLoading?: boolean;
  activeTab: ScoreTab;
  onTabChange: (tab: ScoreTab) => void;
  projects?: RepositoryAnalysis[];
  selectedProject?: RepositoryAnalysis | null;
  onSelectProject?: (project: RepositoryAnalysis) => void;
}

const LANGUAGE_COLORS = [
  'bg-yellow-400',
  'bg-blue-500',
  'bg-emerald-500',
  'bg-purple-500',
  'bg-orange-400',
  'bg-pink-400',
];

export const OverviewRepositoryHeader: React.FC<OverviewRepositoryHeaderProps> = ({
  data,
  jobId,
  isDevelopment,
  showDebugJson,
  onToggleDebugJson,
  isExporting,
  onDownloadReport,
  onCopyAgentMd,
  onDownloadAgentMd,
  agentMdBusy = false,
  agentMdCopied,
  agentMdEnabled,
  maintenanceScore,
  driftData,
  driftLoading = false,
  activeTab,
  onTabChange,
  projects = [],
  selectedProject,
  onSelectProject,
}) => {
  const hasMaintenanceScore = !!maintenanceScore;

  const maintenanceSummary = (() => {
    if (driftLoading) return 'Loading...';
    if (hasMaintenanceScore) {
      const labels: Record<string, string> = {
        A: 'Excellent maintenance health',
        B: 'Good maintenance practices',
        C: 'Fair — some areas need attention',
        D: 'Poor — action recommended',
        F: 'Critical — immediate attention needed',
      };
      return labels[maintenanceScore!.grade] ?? 'Maintenance assessment';
    }
    if (driftData)
      return `${Math.round(driftData.metrics.driftRatio * 100)}% dependencies outdated`;
    return 'No maintenance data available';
  })();

  const scanDate = parseDateOnly(data.scanDate).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="max-w-7xl mx-auto mb-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-slate-200 dark:border-slate-700/50 overflow-hidden">
        {/* Identity row — name + actions */}
        <div className="flex items-center gap-3 px-6 pt-5 pb-3">
          <div className="p-2.5 rounded-xl bg-teal-500 shadow-md shrink-0">
            <FolderGit2 className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            {data.repositoryId ? (
              <Link
                to={`/repository/${data.repositoryId}`}
                className="text-2xl font-bold font-mono text-primary hover:text-teal-600 dark:hover:text-teal-400 transition-colors leading-tight block truncate"
              >
                {data.repositoryName}
              </Link>
            ) : (
              <span className="text-2xl font-bold font-mono text-primary leading-tight block truncate">
                {data.repositoryName}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isDevelopment && (
              <button
                onClick={onToggleDebugJson}
                className="p-1.5 rounded-md text-secondary hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                title={showDebugJson ? 'Hide JSON' : 'Show Raw JSON'}
              >
                <Code className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onDownloadReport}
              disabled={isExporting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 dark:border-slate-700 text-secondary hover:text-primary hover:bg-slate-50 dark:hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Open the print dialog to save the SDLC report as PDF"
            >
              {isExporting ? (
                <Loader className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Printer className="w-3.5 h-3.5" />
              )}
              Download report
            </button>
            {agentMdEnabled && (
              <div className="flex items-center">
                <button
                  onClick={onDownloadAgentMd}
                  disabled={agentMdBusy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-l-lg text-xs font-medium border border-slate-200 dark:border-slate-700 text-secondary hover:text-primary hover:bg-slate-50 dark:hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Download the agent instructions as Markdown"
                >
                  <FileDown className="w-3.5 h-3.5" />
                  Download agent instructions
                </button>
                <button
                  onClick={onCopyAgentMd}
                  disabled={agentMdBusy}
                  className={`flex items-center px-2 py-1.5 rounded-r-lg text-xs font-medium border border-l-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    agentMdCopied
                      ? 'border-green-500 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
                      : 'border-slate-200 dark:border-slate-700 text-secondary hover:text-primary hover:bg-slate-50 dark:hover:bg-slate-700/50'
                  }`}
                  title={
                    agentMdCopied
                      ? 'Copied!'
                      : 'Copy the agent instructions to paste into your AI coding agent'
                  }
                >
                  {agentMdCopied ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            )}
            <Link
              to={`/jobs/${jobId}/kg`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-teal-500 hover:bg-teal-600 transition-colors"
              title="Visualize your repo"
            >
              <Network className="w-3.5 h-3.5" />
              Visualize
            </Link>
          </div>
        </div>

        {/* Description */}
        {data.repositoryDetails?.description && (
          <p className="px-6 pb-4 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            {data.repositoryDetails.description}
          </p>
        )}

        {/* Meta — TYPE + SCANNED + REF as label/value pairs */}
        {(data.repositoryDetails?.projectType || data.scanDate) && (
          <div className="flex items-start gap-10 px-6 pb-5">
            {data.repositoryDetails?.projectType && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-secondary/35 mb-0.5">
                  Type
                </p>
                <p className="text-sm font-bold text-primary capitalize">
                  {data.repositoryDetails.projectType.replace(/_/g, ' ')}
                </p>
              </div>
            )}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-secondary/35 mb-0.5">
                Scanned
              </p>
              <p className="text-sm font-bold text-primary">{scanDate}</p>
            </div>
            {data.scannedBranch && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-secondary/35 mb-0.5">
                  Branch
                </p>
                <p className="text-sm font-bold text-primary font-mono">{data.scannedBranch}</p>
              </div>
            )}
            {!data.scannedBranch && data.scannedCommitSha && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-secondary/35 mb-0.5">
                  Commit
                </p>
                <p className="text-sm font-bold text-primary font-mono">
                  {data.scannedCommitSha.slice(0, 7)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Language bar + legend — only for single-repo view */}
        {projects.length <= 1 && data.languages && data.languages.length > 0 && (
          <div className="border-t border-slate-100 dark:border-slate-700/40">
            <div className="px-6 pt-3">
              <div className="flex w-full h-2.5 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-700/50">
                {data.languages.slice(0, 6).map((lang, i) => {
                  const pct = typeof lang === 'object' ? lang.percentage || 0 : 0;
                  return (
                    <div
                      key={i}
                      className={`${LANGUAGE_COLORS[i % LANGUAGE_COLORS.length]} h-full`}
                      style={{ width: `${pct}%` }}
                    />
                  );
                })}
              </div>
            </div>
            <div className="px-6 pt-2 pb-1">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                {data.languages.slice(0, 6).map((lang, i) => {
                  const name = typeof lang === 'string' ? lang : lang.name;
                  const pct = typeof lang === 'object' && lang.percentage ? lang.percentage : null;
                  return (
                    <span key={i} className="flex items-center gap-1.5 text-xs">
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${LANGUAGE_COLORS[i % LANGUAGE_COLORS.length]}`}
                      />
                      <span className="text-primary font-bold">{name}</span>
                      {pct !== null && <span className="text-secondary/60">{pct}%</span>}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-6 pb-4 pt-2 flex-wrap">
              <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700/50 text-secondary/45 tabular-nums">
                {formatCount(data.filesCount)} files
              </span>
              {(data.strengths?.length ?? 0) > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded tabular-nums text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-400/10">
                  {data.strengths!.length} {data.strengths!.length === 1 ? 'strength' : 'strengths'}
                </span>
              )}
              {(data.gaps?.length ?? 0) > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded tabular-nums text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-400/10">
                  {data.gaps!.length} {data.gaps!.length === 1 ? 'gap' : 'gaps'}
                </span>
              )}
              {(data.recommendations?.length ?? 0) > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded tabular-nums text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-400/10">
                  {data.recommendations!.length} recommendations
                </span>
              )}
            </div>
          </div>
        )}

        {/* Project selector — only when >1 project */}
        <ProjectList
          projects={projects}
          selectedProject={selectedProject}
          onSelectProject={onSelectProject}
        />
      </div>

      {/* View tabs — own card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xs border border-slate-200 dark:border-slate-700/50 overflow-hidden mt-3 grid grid-cols-2">
        <div className="flex border-r border-slate-100 dark:border-slate-700/40">
          <ScoreCircle
            label="SDLC"
            icon={<Code2 className="w-4 h-4" />}
            summary="Development practices quality"
            isActive={activeTab === 'sdlc'}
            onClick={() => onTabChange('sdlc')}
          />
        </div>
        <div className="flex">
          <ScoreCircle
            label="Drift"
            icon={<Activity className="w-4 h-4" />}
            summary={maintenanceSummary}
            isActive={activeTab === 'drift'}
            onClick={() => onTabChange('drift')}
          />
        </div>
      </div>
    </div>
  );
};
